import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, Droplets } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ea404883-7061-4aba-83d0-2d4ab5ad0223/artifacts/ffswoxpp_69d6c27f-288c-44ba-8ef2-7664150a7535.jpeg";

const navLinks = [
  { name: 'Inicio', path: '/' },
  { name: 'Servicios', path: '/servicios' },
  { name: 'Zona', path: '/zona' },
  { name: 'Nosotros', path: '/nosotros' },
  { name: 'Contacto', path: '/contacto' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      setIsScrolled(currentScrollY > 50);
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: 0 }}
        animate={{ y: isVisible ? 0 : -100 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'glass shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="nav-logo"
            >
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <img 
                  src={LOGO_URL} 
                  alt="Piscinas Genovés" 
                  className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-lg sm:rounded-xl"
                />
              </motion.div>
              <div className="hidden sm:block">
                <span className="font-manrope font-bold text-lg sm:text-xl text-sky-500">
                  Piscinas
                </span>
                <span className="font-manrope font-bold text-lg sm:text-xl text-slate-800 ml-1">
                  Genovés
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`relative px-4 py-2 font-medium text-sm transition-colors ${
                    location.pathname === link.path
                      ? 'text-sky-500'
                      : 'text-slate-700 hover:text-sky-500'
                  }`}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute bottom-0 left-2 right-2 h-0.5 bg-sky-500 rounded-full"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <a 
                href="tel:+34634060878" 
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-500 transition-colors"
                data-testid="nav-phone"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden lg:inline">+34 634 06 08 78</span>
              </a>
              <Link
                to="/contacto"
                data-testid="nav-cta"
                className="liquid-btn bg-sky-500 hover:bg-sky-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-all shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40"
              >
                Presupuesto
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-xl hover:bg-sky-50 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-700" />
              ) : (
                <Menu className="w-6 h-6 text-slate-700" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden"
            data-testid="mobile-menu"
          >
            <div 
              className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-2xl"
            >
              <div className="p-6 pt-24">
                <div className="flex flex-col gap-2">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={link.path}
                        data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                        className={`flex items-center gap-3 p-4 rounded-2xl transition-all ${
                          location.pathname === link.path
                            ? 'bg-sky-50 text-sky-500'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Droplets className="w-5 h-5" />
                        <span className="font-semibold">{link.name}</span>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-slate-100">
                  <a 
                    href="tel:+34634060878"
                    className="flex items-center gap-3 p-4 rounded-2xl text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    <Phone className="w-5 h-5 text-sky-500" />
                    <span>+34 634 06 08 78</span>
                  </a>
                  <a 
                    href="mailto:genovespiscinas@gmail.com"
                    className="flex items-center gap-3 p-4 rounded-2xl text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    <Mail className="w-5 h-5 text-sky-500" />
                    <span>genovespiscinas@gmail.com</span>
                  </a>
                </div>

                <Link
                  to="/contacto"
                  className="mt-6 block w-full text-center bg-sky-500 hover:bg-sky-600 text-white py-4 rounded-full font-semibold transition-all shadow-lg shadow-sky-500/25"
                >
                  Solicitar Presupuesto
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
